/**
 * Priced Scenarios — every persisted sizer scenario, one row each.
 *
 * Every priced run (RTL/GUC and DSCR; quick checks are not priced, so they are
 * not stored) persists server-side as a `pricing_scenarios` doc with an
 * append-only `versions[]` history. The sizers write that record; this list is
 * how a scenario is found again.
 *
 *   GET  /pricing/scenarios?status=&user=  the rows. Non-admins see their OWN
 *                                          scenarios; admins see everyone's, and
 *                                          `user` narrows to one person.
 *
 * A row opens the scenario WORKSPACE (sizersShared/workspace) — the matching
 * sizer at `?scenario=<id>` — in Review mode; the "Edit &
 * re-price" opens it in Edit mode. That page carries everything the old
 * drilldown did (rename, version history, archive, raw payload), so this list
 * is only the list.
 *
 * Layout (Marq-style): status tabs + "Only my scenarios" on the card's top row,
 * search + type chips + "x of y shown" on the toolbar under it, then the grid
 * and a count footer. The list route carries the CURRENT inputs but not the
 * result, so a row's amount and leverage are what was ASKED for.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import AddRounded from "@mui/icons-material/AddRounded";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";
import SearchOffRounded from "@mui/icons-material/SearchOffRounded";
import { useSnackbar } from "notistack";

import AgGridTableWrapper from "../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { sizerApi, readScenarioId } from "../../../api/sizerApi";
import { useReduxHook } from "../../../hooks/useReduxHook";
import { useEnsureUsers } from "../../../hooks/useEnsureReferenceData";
import { getUserFullNameFromEmail } from "../../../utils/functions/getUserFullNameFromEmail";
import {
  formatDateTime,
  timestampMs,
} from "../../../_src_shared/utils/functions/formatDateTime";
import { slate } from "../../../theme/colors";
import {
  SZ,
  SizerShell,
  SurfaceCard,
  EmptyState,
  PrimaryButton,
} from "../../sizersShared/sizerUi";
import { fmtMoney, fmtPct } from "../../sizersShared/sizerFormat";
import {
  TYPE_META,
  scenarioKind,
  errText,
  idOf,
  scenarioRequestedAmount,
  scenarioRequestedLeverage,
  scenarioSubtitle,
  sizerPathFor,
  typeMeta,
} from "../../sizersShared/scenarioModel";
import { SIZER_TABS, DSCR_SIZER_PATH } from "../../../AuthenticatedDashboard/sizersNav";
import { useCanUseDscrSizer } from "../../../hooks/useCanUseDscrSizer";
import {
  FilterChip,
  NUM,
  OnlyMineSwitch,
  PersonCell,
  ScenarioStatusPill,
  SearchBox,
  StatusTabs,
  TypeTag,
} from "./PricedScenariosParts";

// No "All" tab: archived and converted rows mixed into the default view are
// noise. The list opens on Active.
const STATUS_TABS = ["Active", "Converted", "Archived"];

// One option per SIZER: the model's `scenarioType` enum (rtl / dscr_quick)
// split by product, since RTL and GUC share "rtl" (see scenarioKind). The
// filter is client-side, so "guc" is never sent anywhere.
const TYPE_FILTERS = ["all", ...Object.keys(TYPE_META)];

// "New scenario" opens one of the pricing sizers, blank.
const NEW_SCENARIO_TARGETS = SIZER_TABS.filter((t) =>
  ["/sizers/rtl", "/sizers/guc", "/sizers/dscr"].includes(t.to),
);

const pricedMs = (r) => timestampMs(r?.pricedAt || r?.updatedAt) || 0;

/** "Today, 5:23 PM" / "Yesterday, 3:44 PM" / "Oct 8, 3:44 PM" — Eastern. */
const recentLabel = (iso) => {
  if (!iso) return "—";
  const day = formatDateTime(iso, "yyyy-MM-dd");
  const now = new Date();
  const today = formatDateTime(now.toISOString(), "yyyy-MM-dd");
  const yesterday = formatDateTime(
    new Date(now.getTime() - 86400000).toISOString(),
    "yyyy-MM-dd",
  );
  const time = formatDateTime(iso, "h:mm a");
  if (day === today) return `Today, ${time}`;
  if (day === yesterday) return `Yesterday, ${time}`;
  return day.slice(0, 4) === today.slice(0, 4)
    ? formatDateTime(iso, "MMM d, h:mm a")
    : formatDateTime(iso, "MMM d, yyyy");
};

/**
 * The card takes whatever viewport is left below its top edge — this is a
 * pipeline you scan, so a short window shows fewer rows, not the same ten with
 * the page scrolled.
 */
function useFillViewportHeight(
  ref,
  deps = [],
  { min = 360, gutter = 28 } = {},
) {
  const [height, setHeight] = useState(min);
  useEffect(() => {
    const measure = () => {
      const top = ref.current?.getBoundingClientRect().top;
      if (top == null) return;
      setHeight(Math.max(min, Math.round(window.innerHeight - top - gutter)));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, min, gutter, ...deps]);
  return height;
}

/* ============================================================ Legacy link */

/**
 * `?id=<scenario>` was the old drilldown. The scenario now lives on its
 * sizer's workspace page, and which sizer that is depends on the doc — so read
 * it once and forward.
 */
function LegacyScenarioRedirect({ scenarioId, onMissing }) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    let live = true;
    sizerApi
      .getScenario(scenarioId)
      .then((doc) => {
        if (!live) return;
        const path = sizerPathFor(scenarioKind(doc), scenarioId, null, {
          mode: "review",
        });
        if (path) navigate(path, { replace: true });
        else onMissing();
      })
      .catch((e) => {
        if (!live) return;
        enqueueSnackbar(errText(e, "Could not open that scenario"), {
          variant: "error",
        });
        onMissing();
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId]);
  return (
    <Flx center g={1.5} sx={{ py: 8 }}>
      <CircularProgress size={20} sx={{ color: SZ.teal700 }} />
      <Txt sx={{ fontSize: 13, color: SZ.ink500 }}>Opening scenario…</Txt>
    </Flx>
  );
}

/* ============================================================ The screen */

export default function PricedScenariosScreen() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin, userEmail } = useReduxHook();
  // DSCR is super-admin only: everyone else gets no DSCR rows, chip or "New
  // scenario" entry — a row they could see but not open would be a dead end.
  const canUseDscr = useCanUseDscrSizer();
  const typeFilters = useMemo(
    () => TYPE_FILTERS.filter((t) => canUseDscr || t !== "dscr_quick"),
    [canUseDscr],
  );
  const newTargets = useMemo(
    () => NEW_SCENARIO_TARGETS.filter((t) => canUseDscr || t.to !== DSCR_SIZER_PATH),
    [canUseDscr],
  );
  const allowedKind = useCallback(
    (r) => canUseDscr || scenarioKind(r) !== "dscr_quick",
    [canUseDscr],
  );
  // Names for the Priced by column; falls back to the email.
  useEnsureUsers();
  const users = useSelector((state) => state.users);
  const nameOf = useCallback(
    (email) => (email ? getUserFullNameFromEmail(users, email) || email : null),
    [users],
  );

  const [params, setParams] = useSearchParams();
  const legacyId = readScenarioId(params.get("id"));

  // Both filters that change WHICH rows the server returns live in the URL, so
  // a filtered list is linkable and Back from a scenario lands on it again.
  const statusParam = params.get("status");
  const status = STATUS_TABS.includes(statusParam) ? statusParam : "Active";
  // Admin-only: the endpoint scopes a non-admin to their own scenarios
  // whatever is asked for, so for them the switch would change nothing.
  const onlyMine = isAdmin && params.get("mine") === "1";
  const setParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (value == null || value === "") p.delete(key);
    else p.set(key, value);
    setParams(p, { replace: true });
  };

  // `allRows` (no status) feeds the tab counts; `statusRows` is the tab's own
  // request, so a status past the newest-200 cap still lists completely.
  const [allRows, setAllRows] = useState(null);
  const [statusRows, setStatusRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [newAnchor, setNewAnchor] = useState(null);
  const searchRef = useRef(null);
  const cardRef = useRef(null);

  const load = useCallback(() => {
    if (legacyId) return;
    setLoading(true);
    const base = onlyMine && userEmail ? { user: userEmail } : {};
    const sortNewest = (list) =>
      [...list].sort((a, b) => pricedMs(b) - pricedMs(a));
    Promise.all([
      sizerApi.listScenarios(base),
      sizerApi.listScenarios({ ...base, status }),
    ])
      .then(([all, byStatus]) => {
        setAllRows(sortNewest(all));
        setStatusRows(sortNewest(byStatus));
      })
      .catch((e) => {
        setAllRows([]);
        setStatusRows([]);
        enqueueSnackbar(errText(e, "Failed to load scenarios"), {
          variant: "error",
        });
      })
      .finally(() => setLoading(false));
  }, [legacyId, onlyMine, userEmail, status, enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  // No Refresh button: the list reads again on arrival and whenever the tab
  // regains focus, which is when a reprice made elsewhere would have landed.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  // `/` jumps to search from anywhere that isn't already a text field.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      if (
        t?.isContentEditable ||
        ["INPUT", "TEXTAREA", "SELECT"].includes(t?.tagName)
      )
        return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ------------------------------------------------------------ filtering */

  const matchesQuery = useCallback(
    (r) => {
      const needle = query.trim().toLowerCase();
      if (!needle) return true;
      return [
        r.name,
        r.createdBy,
        nameOf(r.createdBy),
        r.createdByCompany,
        r.dealId,
        idOf(r),
        typeMeta(scenarioKind(r)).label,
        scenarioSubtitle(r.scenarioType, r.scenario),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle);
    },
    [query, nameOf],
  );
  const matchesType = useCallback(
    (r) => typeFilter === "all" || scenarioKind(r) === typeFilter,
    [typeFilter],
  );

  // Counts react to the other filters: each number says how many rows picking
  // that option would show.
  const statusCounts = useMemo(() => {
    const c = Object.fromEntries(STATUS_TABS.map((s) => [s, 0]));
    (allRows || [])
      .filter((r) => allowedKind(r) && matchesQuery(r) && matchesType(r))
      .forEach((r) => {
        if (c[r.status] != null) c[r.status] += 1;
      });
    return c;
  }, [allRows, allowedKind, matchesQuery, matchesType]);

  const tabRows = useMemo(
    () => (statusRows || []).filter((r) => r.status === status && allowedKind(r)),
    [statusRows, status, allowedKind],
  );
  const typeCounts = useMemo(() => {
    const c = Object.fromEntries(typeFilters.map((t) => [t, 0]));
    tabRows.filter(matchesQuery).forEach((r) => {
      c.all += 1;
      const k = scenarioKind(r);
      if (c[k] != null) c[k] += 1;
    });
    return c;
  }, [tabRows, matchesQuery, typeFilters]);

  const visible = useMemo(
    () => tabRows.filter((r) => matchesType(r) && matchesQuery(r)),
    [tabRows, matchesType, matchesQuery],
  );

  const filtersActive =
    typeFilter !== "all" || Boolean(query.trim()) || onlyMine;
  const clearFilters = () => {
    setTypeFilter("all");
    setQuery("");
    if (onlyMine) setParam("mine", null);
  };

  /* ------------------------------------------------------------ rows */

  const rows = useMemo(
    () =>
      visible.map((r) => {
        const kind = scenarioKind(r);
        return {
          id: String(idOf(r)),
          kind,
          typeLabel: typeMeta(kind).label,
          name: r.name,
          subtitle: scenarioSubtitle(r.scenarioType, r.scenario),
          status: r.status,
          amount: scenarioRequestedAmount(r.scenarioType, r.scenario),
          leverage: scenarioRequestedLeverage(r.scenarioType, r.scenario),
          versions: r.currentVersion || 1,
          pricedAt: r.pricedAt || r.updatedAt,
          owner: r.createdBy,
        };
      }),
    [visible],
  );

  const totalVersions = useMemo(
    () => visible.reduce((n, r) => n + (r.currentVersion || 1), 0),
    [visible],
  );

  const openScenario = useCallback(
    (row, mode) => {
      const path = sizerPathFor(row.kind, row.id, null, { mode });
      if (path) navigate(path);
    },
    [navigate],
  );

  /* ------------------------------------------------------------ columns */

  const columnDefs = useMemo(
    () => [
      {
        colId: "scenario",
        headerName: "Scenario",
        flex: 2.2,
        minWidth: 280,
        cellRenderer: (p) => {
          const d = p.data;
          return (
            <Flx
              column
              sx={{
                height: "100%",
                justifyContent: "center",
                minWidth: 0,
                lineHeight: 1.35,
              }}
            >
              <Flx ac g={0.875} sx={{ minWidth: 0 }}>
                <TypeTag>{d.typeLabel}</TypeTag>
                <Txt
                  component="div"
                  sx={{
                    fontSize: 14,
                    fontWeight: d.name ? 600 : 400,
                    fontStyle: d.name ? "normal" : "italic",
                    color: d.name ? SZ.ink900 : SZ.ink500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {d.name || "Untitled scenario"}
                </Txt>
              </Flx>
              <Txt
                component="div"
                sx={{
                  fontSize: 12.5,
                  color: SZ.ink500,
                  mt: "2px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {d.subtitle || "No property or terms entered"}
              </Txt>
            </Flx>
          );
        },
      },
      {
        colId: "status",
        headerName: "Status",
        width: 130,
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%" }}>
            <ScenarioStatusPill status={p.data.status} />
          </Flx>
        ),
      },
      {
        colId: "amount",
        headerName: "Loan amount",
        width: 140,
        headerClass: "ag-right-aligned-header",
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%", justifyContent: "flex-end" }}>
            <Txt
              sx={{
                fontFamily: SZ.mono,
                fontSize: 13.5,
                fontWeight: 600,
                color: p.data.amount == null ? SZ.ink400 : SZ.ink900,
                ...NUM,
              }}
            >
              {fmtMoney(p.data.amount)}
            </Txt>
          </Flx>
        ),
      },
      {
        colId: "leverage",
        headerName: "Req. lev.",
        headerTooltip:
          "RTL / GUC requested leverage from the inputs. DSCR sizes by loan amount.",
        width: 105,
        headerClass: "ag-right-aligned-header",
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%", justifyContent: "flex-end" }}>
            <Txt
              sx={{
                fontFamily: SZ.mono,
                fontSize: 13,
                color: p.data.leverage == null ? SZ.ink400 : SZ.ink900,
                ...NUM,
              }}
            >
              {p.data.leverage == null ? "—" : fmtPct(p.data.leverage, 1)}
            </Txt>
          </Flx>
        ),
      },
      {
        colId: "versions",
        headerName: "Versions",
        headerTooltip:
          "How many times this scenario has been priced. Switch versions inside the scenario.",
        width: 105,
        cellRenderer: (p) => (
          <Flx
            ac
            g={0.5}
            sx={{ height: "100%", color: SZ.ink600, fontSize: 13, ...NUM }}
          >
            <HistoryRounded sx={{ fontSize: 16, color: SZ.ink400 }} />
            {p.data.versions}
          </Flx>
        ),
      },
      {
        colId: "priced",
        headerName: "Last priced",
        width: 160,
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%" }}>
            <Txt
              title={formatDateTime(p.data.pricedAt) || undefined}
              sx={{ fontSize: 13, color: SZ.ink600, ...NUM }}
            >
              {recentLabel(p.data.pricedAt)}
            </Txt>
          </Flx>
        ),
      },
      {
        colId: "owner",
        headerName: "Priced by",
        flex: 1,
        minWidth: 180,
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%", minWidth: 0 }}>
            <PersonCell name={nameOf(p.data.owner)} email={p.data.owner} />
          </Flx>
        ),
      },
      {
        // Always visible. The whole row already opens the scenario, so this is the
        // shortcut straight into Edit mode rather than a second "open" button
        // repeated on every row.
        colId: "edit",
        headerName: "",
        width: 160,
        resizable: false,
        cellStyle: { paddingRight: 12 },
        cellRenderer: (p) => (
          <Flx ac sx={{ height: "100%", justifyContent: "flex-end" }}>
            <Box
              component="button"
              type="button"
              className="row-edit-btn"
              onClick={(e) => {
                e.stopPropagation();
                openScenario(p.data, "edit");
              }}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                height: 32,
                px: 1.25,
                border: `1px solid ${SZ.teal100}`,
                borderRadius: "7px",
                bgcolor: "#fff",
                color: SZ.teal700,
                font: "inherit",
                fontSize: 12.5,
                fontWeight: 600,
                whiteSpace: "nowrap",
                cursor: "pointer",
                "& svg": { fontSize: 15 },
                "&:hover": { bgcolor: SZ.teal50, borderColor: SZ.teal500 },
                "&:focus-visible": {
                  opacity: 1,
                  outline: `2px solid ${SZ.teal500}`,
                  outlineOffset: 2,
                },
              }}
            >
              <EditOutlined />
              Edit &amp; re-price
            </Box>
          </Flx>
        ),
      },
    ],
    [nameOf, openScenario],
  );

  /* ------------------------------------------------------------ layout */

  const empty = statusRows != null && !visible.length;
  const cardHeight = useFillViewportHeight(cardRef, [allRows, empty]);

  if (legacyId) {
    return (
      <SizerShell title="Priced Scenarios">
        <LegacyScenarioRedirect
          scenarioId={legacyId}
          onMissing={() => setParam("id", null)}
        />
      </SizerShell>
    );
  }

  const tabEmpty = statusRows != null && !tabRows.length;

  return (
    <SizerShell
      title="Priced Scenarios"
      subtitle="Every priced sizer run. Click a row to open it — review results or edit and re-price in one place."
      pb="40px"
      headerAction={
        <>
          <PrimaryButton
            onClick={(e) => setNewAnchor(e.currentTarget)}
            startIcon={<AddRounded />}
            sx={{ height: 38, minHeight: 38, px: "16px", fontSize: 13.5 }}
          >
            New scenario
            <ExpandMoreRounded sx={{ ml: -0.5 }} />
          </PrimaryButton>
          <Menu
            anchorEl={newAnchor}
            open={Boolean(newAnchor)}
            onClose={() => setNewAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{ paper: { sx: { mt: 0.75, minWidth: 180 } } }}
          >
            {newTargets.map((t) => (
              <MenuItem
                key={t.to}
                onClick={() => {
                  setNewAnchor(null);
                  navigate(t.to);
                }}
                sx={{ fontSize: 13.5 }}
              >
                {t.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      }
    >
      <SurfaceCard
        sx={{
          display: "flex",
          flexDirection: "column",
          ...(empty ? null : { height: cardHeight }),
        }}
      >
        <Box ref={cardRef} />

        {/* ── Status tabs + Only mine ───────────────────────────────────── */}
        <Flx
          jb
          ac
          g={2}
          sx={{
            px: 2.5,
            borderBottom: `1px solid ${SZ.line}`,
            flexWrap: "wrap",
          }}
        >
          <StatusTabs
            value={status}
            onChange={(s) => setParam("status", s === "Active" ? null : s)}
            options={STATUS_TABS.map((s) => ({
              value: s,
              label: s,
              count: statusCounts[s],
            }))}
          />
          {isAdmin && (
            <OnlyMineSwitch
              checked={onlyMine}
              onChange={(on) => setParam("mine", on ? "1" : null)}
            />
          )}
        </Flx>

        {/* ── Search · type chips · clear · count ───────────────────────── */}
        <Flx
          ac
          g={1.5}
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: `1px solid ${SZ.line}`,
            flexWrap: "wrap",
            bgcolor: "#fcfdfd",
          }}
        >
          <SearchBox
            ref={searchRef}
            value={query}
            onChange={setQuery}
            placeholder="Search name, address, user, loan id…"
          />
          <Box
            sx={{
              width: "1px",
              height: 26,
              bgcolor: SZ.line,
              display: { xs: "none", sm: "block" },
            }}
          />
          <Flx ac g={1} sx={{ flexWrap: "wrap" }}>
            {typeFilters.map((t) => (
              <FilterChip
                key={t}
                on={typeFilter === t}
                label={t === "all" ? "All types" : TYPE_META[t].label}
                count={typeCounts[t] ?? 0}
                onClick={() => setTypeFilter(t)}
              />
            ))}
          </Flx>
          {filtersActive && (
            <Box
              component="button"
              type="button"
              onClick={clearFilters}
              sx={{
                border: 0,
                background: "none",
                p: 0,
                font: "inherit",
                fontSize: 13,
                fontWeight: 600,
                color: SZ.teal700,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Clear filters
            </Box>
          )}
          <Txt
            sx={{
              ml: "auto",
              fontSize: 12.5,
              color: SZ.ink500,
              display: { xs: "none", md: "inline" },
              ...NUM,
            }}
          >
            {loading && statusRows == null
              ? "Loading…"
              : `${visible.length} of ${tabRows.length} shown`}
          </Txt>
        </Flx>

        {/* ── Grid / empty ──────────────────────────────────────────────── */}
        {empty ? (
          tabEmpty && !query.trim() && typeFilter === "all" ? (
            <Box sx={{ p: "28px 20px" }}>
              <EmptyState
                icon={<HistoryRounded />}
                title={
                  status === "Active"
                    ? "No scenarios yet"
                    : `No ${status.toLowerCase()} scenarios`
                }
                description={
                  status === "Converted"
                    ? "Scenarios saved as deals land here."
                    : status === "Archived"
                      ? "Archived scenarios land here, with their history kept."
                      : "Price a scenario in one of the sizers and it is saved here, with every reprice kept as a version."
                }
              />
            </Box>
          ) : (
            <Flx column ac sx={{ p: 4, textAlign: "center" }}>
              <SearchOffRounded sx={{ fontSize: 32, color: SZ.ink400 }} />
              <Txt
                component="div"
                sx={{ fontSize: 15, fontWeight: 700, color: SZ.ink900, mt: 1 }}
              >
                No {status.toLowerCase()} scenarios match
              </Txt>
              <Txt
                component="div"
                sx={{ fontSize: 13.5, color: SZ.ink500, mt: 0.5 }}
              >
                Try a different search or type, or{" "}
                <Box
                  component="button"
                  type="button"
                  onClick={clearFilters}
                  sx={{
                    p: 0,
                    border: 0,
                    background: "none",
                    font: "inherit",
                    color: SZ.teal700,
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  clear the filters
                </Box>
                .
              </Txt>
            </Flx>
          )
        ) : (
          <>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <AgGridTableWrapper
                fullHeight
                noBorder
                noBorderRadius
                sx={{
                  "--ag-header-background-color": "#fcfdfd",
                  "--ag-header-foreground-color": slate[500],
                  "--ag-font-size": "13px",
                  "--ag-row-hover-color": SZ.teal50,
                  "& .ag-header-cell-text": { fontWeight: 600, fontSize: 12.5 },
                }}
              >
                <AgGridReact
                  rowData={rows}
                  columnDefs={columnDefs}
                  defaultColDef={{
                    sortable: false,
                    resizable: true,
                    suppressHeaderMenuButton: true,
                    suppressHeaderFilterButton: true,
                  }}
                  headerHeight={42}
                  rowHeight={60}
                  getRowId={(p) => p.data.id}
                  rowStyle={{ cursor: "pointer" }}
                  onRowClicked={(e) => openScenario(e.data, "review")}
                  loading={loading && statusRows == null}
                  suppressCellFocus
                />
              </AgGridTableWrapper>
            </Box>
            <Flx
              jb
              ac
              g={1}
              sx={{
                flexWrap: "wrap",
                px: 2.5,
                py: 1.5,
                borderTop: `1px solid ${SZ.line}`,
                fontSize: 12.5,
                color: SZ.ink500,
                ...NUM,
              }}
            >
              <span>
                {visible.length} of {tabRows.length} scenario
                {tabRows.length === 1 ? "" : "s"} · {totalVersions} priced
                version{totalVersions === 1 ? "" : "s"}
              </span>
              <span>
                Click a row to review it · Edit &amp; re-price opens the inputs
              </span>
            </Flx>
          </>
        )}
      </SurfaceCard>
    </SizerShell>
  );
}
